import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  ModalFooter,
  Icon,
  Flex,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPassportDetailsRequest } from "../hcfStore/actions";
import PrimaryButton from "../../../components/PrimaryButton";
import Iframe from "react-iframe";
import PrimaryText from "../../../components/PrimaryText";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import FormInput from "../../../components/FormInput";
import FormSelector from "../../../components/FormSelector";

const PassportModal = ({
  isOpen,
  onClose,
  trDetails = {},
  isPatientPassport = true,
  handleSubmit = () => {},
  patientPassport,
  attendantDetails,
}) => {
  const dispatch = useDispatch();
  const { passportDetails, countryData } = useSelector((state) => state.hcf);

  const [attendantIndex, setAttendantIndex] = useState(0);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [passportError, setPassportError] = useState("");
  const [dobError, setDobError] = useState("");

  const [editableDetails, setEditableDetails] = useState({
    name: "",
    passportNo: "",
    nationality: "",
    dob: "",
    passportImage: "",
  });

  useEffect(() => {
    if (passportDetails) {
      if (isPatientPassport && isPatientPassport !== "") {
        setEditableDetails({
          name: passportDetails.name,
          passportNo: passportDetails.passport_number,
          nationality: {
            label: passportDetails.nationality,
            value: passportDetails.nationality,
          },
          dob: passportDetails.dob.split("-").reverse().join("-"),
        });
      } else {
        setEditableDetails({
          name: passportDetails.name,
          passportNo: passportDetails.passport_number,
        });
      }
    }
  }, [passportDetails]);

  useEffect(() => {
    if (isPatientPassport && isPatientPassport !== "") {
      setEditableDetails({
        name: trDetails.patient_name,
        passportNo: trDetails.passport_no,
        nationality: {
          label: trDetails.nationality,
          value: trDetails.nationality,
        },
        dob: trDetails.dob,
        passportImage: patientPassport,
      });
    } else {
      if (attendantDetails.length > 0) {
        updateAttendantDetails(attendantIndex);
      }
    }
  }, [trDetails, isPatientPassport, attendantIndex]);

  const updateAttendantDetails = (index) => {
    const currentAttendant = attendantDetails[index];
    setEditableDetails({
      name: currentAttendant.attendant_name,
      passportNo: currentAttendant.passport_no,
      passportImage: currentAttendant.attendant_passport,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === "passportNo") {
      setPassportError("");
    }
    if (name === "dob") {
      setDobError("");
    }
  };

  const handleSelectChange = (e) => {
    setEditableDetails((prevState) => ({
      ...prevState,
      nationality: {
        label: e.label,
        value: e.value,
      },
    }));
  };

  const nextAttendant = () => {
    if (attendantIndex < attendantDetails.length - 1) {
      setAttendantIndex(attendantIndex + 1);
    }
  };

  const previousAttendant = () => {
    if (attendantIndex > 0) {
      setAttendantIndex(attendantIndex - 1);
    }
  };

  const handlePassportValidation = () => {
    const attendantPassports = trDetails.attendant_details.map(
      (attendant) => attendant.passport_no
    );
    const patientPassport = trDetails.passport_no;
    if (!isPatientPassport) {
      if (editableDetails.passportNo === patientPassport) {
        setPassportError(
          "Attendant passport and patient passport cannot be the same."
        );
        return false;
      }

      const isDuplicateAttendantPassport = attendantPassports.includes(
        editableDetails.passportNo
      );

      if (isDuplicateAttendantPassport) {
        setPassportError(
          "Attendant with the same passport number already exists."
        );
        return false;
      }
    }

    if (isPatientPassport && isPatientPassport !== "") {
      const isDuplicatePatientPassport = attendantPassports.includes(
        editableDetails.passportNo
      );
      if (isDuplicatePatientPassport) {
        setPassportError(
          "This passport number already exists in the attendants list."
        );
        return false;
      }
    }

    setPassportError("");
    return true;
  };

  const handleDOBValidation = () => {
    const selectedDOB = new Date(editableDetails.dob);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(
      today.getFullYear() - 120,
      today.getMonth(),
      today.getDate()
    );
    if (selectedDOB > today) {
      setDobError("The Date of Birth cannot be in the future.");
      return false;
    } else if (selectedDOB < minDate) {
      setDobError("Invalid Date of Birth");
      return false;
    } else {
      setDobError("");
      return true;
    }
  };

  const handleUpdate = () => {
    // Determine if there are changes
    let hasChanges = false;
    let payload = {
      "tr-id": trDetails.id,
      isPatientPassport: isPatientPassport,
    };

    // Check patient details
    if (isPatientPassport) {
      if (
        editableDetails.name !== trDetails.patient_name ||
        editableDetails.passportNo !== trDetails.passport_no ||
        editableDetails.nationality?.value !== trDetails.nationality ||
        editableDetails.dob !== trDetails.dob ||
        uploadedImage
      ) {
        hasChanges = true;
        if (editableDetails.name !== trDetails.patient_name) {
          payload.name = editableDetails.name;
        }
        if (editableDetails.passportNo !== trDetails.passport_no) {
          payload.passportNo = editableDetails.passportNo;
        }
        if (editableDetails.nationality?.value !== trDetails.nationality) {
          payload.nationality = editableDetails.nationality?.value;
        }
        if (editableDetails.dob !== trDetails.dob) {
          payload.dob = editableDetails.dob;
        }
        if (uploadedImage) {
          payload.passportImage = uploadedImage;
        }
      }
    } else {
      // Check attendant details
      const currentAttendant = attendantDetails[attendantIndex];

      if (
        editableDetails.name !== currentAttendant.attendant_name ||
        editableDetails.passportNo !== currentAttendant.passport_no ||
        uploadedImage
      ) {
        hasChanges = true;
        payload.attendantId = currentAttendant.attendant_id;
        if (editableDetails.name !== currentAttendant.attendant_name) {
          payload.name = editableDetails.name;
        }
        if (editableDetails.passportNo !== currentAttendant.passport_no) {
          payload.passportNo = editableDetails.passportNo;
        }
        if (uploadedImage) {
          payload.passportImage = uploadedImage;
        }
      }
    }
    if (hasChanges) {
      if (!handlePassportValidation() || !handleDOBValidation()) {
        return;
      }
      handleSubmit(payload);
      setPassportError("");
    }

    // Notify if no changes found
    if (!hasChanges) {
      alert("No changes found");
      return;
    }

    setPassportError("");

    // Close modal after submission
    onClose();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedImage(file);
      const payload = new FormData();
      payload.append("file", file);
      dispatch(fetchPassportDetailsRequest(payload));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setPassportError("");
        onClose();
      }}
      size="4xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="scroll">
        <ModalHeader color="primary.1" textAlign={"center"} fontSize={"2xl"}>
          {isPatientPassport
            ? "Patient Passport"
            : `Attender ${attendantIndex + 1}/${attendantDetails.length}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex justify="end" align="end">
            <PrimaryButton
              // mr="10px"
              mb="10px"
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = ".pdf,.jpg,.jpeg,.png";
                input.onchange = handleImageUpload;
                input.click();
              }}
              isDisabled={
                trDetails.vil_status === "Not Requested" ? false : true
              }
            >
              Update Passport
            </PrimaryButton>
          </Flex>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb="20px"
          >
            {/* {!isPatientPassport && attendantDetails.length > 1 && (
              <IconButton
                icon={<ChevronLeftIcon boxSize="24px" />} // Adjust size here
                aria-label="Previous Attendant"
                onClick={previousAttendant}
                isDisabled={attendantIndex === 0}
                position="absolute"
                left="10px"
                backgroundColor="transparent"
                border="none"
                _hover={{ backgroundColor: "transparent" }}
                _active={{ backgroundColor: "transparent" }}
                _focus={{ boxShadow: "none" }}
                boxSize="60px" // Adjust overall button size if needed
              />
            )} */}
            {editableDetails.passportImage || uploadedImage ? (
              <Iframe
                height="250px"
                width="100%"
                src={
                  uploadedImage
                    ? URL.createObjectURL(uploadedImage)
                    : editableDetails.passportImage || ""
                }
                // src={editableDetails.passportImage}
                alt="Passport"
              />
            ) : (
              <p>No passport image available.</p> // Fallback message
            )}
            {/* {!isPatientPassport && attendantDetails.length > 1 && (
              <IconButton
                icon={<ChevronRightIcon />}
                aria-label="Next Attendant"
                onClick={nextAttendant}
                isDisabled={attendantIndex === attendantDetails.length - 1}
                position="absolute"
                right="10px"
              />
            )} */}
            {!isPatientPassport && attendantDetails.length > 1 && (
              <>
                <Icon
                  as={ChevronLeftIcon}
                  boxSize="34px" // Adjust size here
                  position="absolute"
                  left="-4px"
                  top="35%"
                  transform="translateY(-50%)"
                  cursor="pointer"
                  color={attendantIndex === 0 ? "gray.500" : "blue.500"}
                  onClick={previousAttendant}
                  _hover={{ color: "blue.700" }}
                  isDisabled={attendantIndex === 0}
                />

                {/* Next Icon */}
                <Icon
                  as={ChevronRightIcon}
                  boxSize="34px" // Adjust size here
                  position="absolute"
                  right="-4px"
                  top="35%"
                  transform="translateY(-50%)"
                  cursor="pointer"
                  color={
                    attendantIndex === attendantDetails.length - 1
                      ? "gray.500"
                      : "blue.500"
                  }
                  onClick={nextAttendant}
                  _hover={{ color: "blue.700" }}
                  isDisabled={attendantIndex === attendantDetails.length - 1}
                />
              </>
            )}
          </Box>
          <Box>
            <PrimaryText className="modal-text">Full Name</PrimaryText>
            <FormInput
              name="name"
              id="name"
              value={editableDetails.name}
              onChange={handleInputChange}
              placeholder="Full Name"
              variant="filled"
            />
            {/* <Input
              name="name"
              value={editableDetails.name}
              onChange={handleInputChange}
              placeholder="Full Name"
              // variant="filled"
            /> */}
          </Box>
          <Box>
            <PrimaryText className="modal-text">Passport No</PrimaryText>
            {/* <FormControl isInvalid={passportError} className="form-input">
              <Input
                name="passportNo"
                value={editableDetails.passportNo || ""}
                onChange={handleInputChange}
                // borderColor={passportError ? "red.500" : "gray.300"}
                // focusBorderColor={passportError ? "red.500" : "blue.500"}
                variant="filled"
              />
              {passportError && (
                <FormErrorMessage>{passportError}</FormErrorMessage>
              )}
            </FormControl> */}
            <FormInput
              isInvalid={passportError}
              name="passportNo"
              id="passportNo"
              value={editableDetails.passportNo || ""}
              onChange={handleInputChange}
              variant="filled"
              errorMessage={passportError ? passportError : ""}
            />
          </Box>
          {isPatientPassport && (
            <>
              <Box>
                <PrimaryText className="modal-text">Nationality</PrimaryText>
                <FormSelector
                  id="nationality"
                  name="nationality"
                  value={editableDetails.nationality}
                  onChange={(e) => handleSelectChange(e)}
                  placeholder="Select Nationality"
                  options={countryData}
                  menuPortalTarget={false}
                  // variant="filled"
                />
              </Box>

              <Box>
                <PrimaryText className="modal-text">DOB</PrimaryText>
                <FormInput
                  id="dob"
                  name="dob"
                  value={editableDetails.dob}
                  onChange={handleInputChange}
                  placeholder="DOB"
                  type="date"
                  errorMessage={dobError}
                  isInvalid={dobError}
                  variant="filled"
                />
              </Box>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <PrimaryButton
            colorScheme="blue"
            ml="auto"
            onClick={handleUpdate}
            isDisabled={trDetails.vil_status === "Not Requested" ? false : true}
          >
            Update
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PassportModal;
